import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { AlertCircle, Loader2 } from 'lucide-react'
import { api } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'

export interface CancelSubscriptionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentPeriodEnd: Date | null
  tierLabel: string
  onCancelled: () => void
}

export function CancelSubscriptionDialog({
  open,
  onOpenChange,
  currentPeriodEnd,
  tierLabel,
  onCancelled,
}: CancelSubscriptionDialogProps) {
  const [submitting, setSubmitting] = useState(false)
  const { toast } = useToast()

  const endDateLabel = currentPeriodEnd
    ? currentPeriodEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : 'the end of your current period'

  const handleCancel = async () => {
    setSubmitting(true)
    try {
      await api.post('/api/billing/subscription/cancel')
      toast({
        title: 'Cancellation scheduled',
        description: `You'll keep your ${tierLabel} coverage through ${endDateLabel}. You can reactivate anytime before then.`,
      })
      onCancelled()
      onOpenChange(false)
    } catch (err: any) {
      toast({
        title: 'Cancellation failed',
        description: err.response?.data?.message ?? err.message ?? 'Please try again',
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-start gap-3 mb-2">
            <div className="shrink-0 h-10 w-10 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-amber-500" strokeWidth={2} />
            </div>
            <div>
              <DialogTitle className="text-xl font-heading">Cancel {tierLabel}?</DialogTitle>
              <DialogDescription className="mt-1">
                Your coverage continues until <strong className="text-foreground">{endDateLabel}</strong>, then your AI goes dark.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="rounded-xl border bg-muted/30 p-4 space-y-2 text-sm">
          <p className="font-medium text-[13px]">What happens:</p>
          <ul className="space-y-1.5 text-[13px] text-muted-foreground">
            <li className="flex gap-2">
              <span className="text-muted-foreground/60">•</span>
              <span>
                You keep full access through <strong className="text-foreground">{endDateLabel}</strong>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-muted-foreground/60">•</span>
              <span>No further charges after that</span>
            </li>
            <li className="flex gap-2">
              <span className="text-muted-foreground/60">•</span>
              <span>Your phone number is held 30 days before release</span>
            </li>
            <li className="flex gap-2">
              <span className="text-muted-foreground/60">•</span>
              <span>Call transcripts, contacts, and agents are preserved — reactivate anytime to restore</span>
            </li>
          </ul>
        </div>

        <DialogFooter className="gap-2 pt-2 sm:gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Keep my coverage
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleCancel}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Cancelling…
              </>
            ) : (
              'Cancel subscription'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
